// Specs of the archetype-2 (transaction / entity log) documents: KPI strip
// + the top N rows under the active sort. Pure (see ./shared.ts).
import { translate, translateWithVars } from "shared/languages";
import { formatDateOrDash, formatDateTime, formatMoneyOrDash } from "shared/reports/columnCatalogue";
import {
  ClientAnalytics,
  ClientRevenueAnalytics,
  DiscountCodeAnalytics,
  InactiveReason,
  RewardUsageAnalytics,
  SalesPackagesAnalytics,
  SalesServicesAnalytics,
} from "shared/types";
import {
  DISCOUNT_CODE_STATUS_LABELS,
  INACTIVE_REASON_LABELS,
  UPGRADE_LABEL,
  formatCurrency,
  formatUpgrades,
  getBookingStatusLabel,
} from "shared/utils";
import { discountCodeRuleLabel, rewardLabel, rewardProgramLabel } from "shared/utils/loyalty";
import { discountSourceLabel } from "shared/utils/purchaseDiscount";
import { formatTransactionNumber } from "shared/utils/transactionNumber";
import { DocSpec } from "../../pagePlan";
import { joinNotes, taxBasisNote, truncationNote } from "../../reportText";
import { SortMeta } from "../../sort";
import { columns, productNounPlural } from "./shared";

// ── Pendapatan per Sesi ─────────────────────────────────────────────────────

type ServicesRow = SalesServicesAnalytics["table"][number];

// META objects are builders, not module consts: translate() must run per
// render, or the first download's language sticks for the session.
const SERVICES_META = (): SortMeta => ({
  start_time: { label: translate("analyticsPage.columns.start_time"), type: "date" },
  attendees: { label: translate("analyticsPage.columns.attendees"), type: "number" },
  revenue: { label: translate("analyticsPage.columns.booking_revenue"), type: "number" },
});

export const servicesSpec: DocSpec<SalesServicesAnalytics, ServicesRow> = {
  layout: "detail",
  limited: true,
  meta: SERVICES_META,
  table: (data) => data.table,
  columns: () =>
    columns<ServicesRow>([
      { metric: "class", render: (r) => r.class_name },
      { metric: "start_time", render: (r) => formatDateTime(r.start_time) },
      { metric: "instructor_text", render: (r) => r.instructor_name },
      { metric: "attendees", render: (r) => String(r.attendees) },
      { metric: "booking_revenue", render: (r) => formatCurrency(r.revenue) },
    ]),
  kpis: (data) => [
    { value: formatCurrency(data.total_revenue.value), label: translate("analyticsPage.sales.kpi_total_revenue") },
    { value: String(data.sessions_completed.value), label: translate("analyticsPage.sales.kpi_sessions_completed") },
    {
      value: formatCurrency(data.revenue_per_session.value),
      label: translate("analyticsPage.sales.kpi_revenue_per_session"),
    },
  ],
  defaultSort: { key: "start_time", order: "descend" },
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.sessions_completed.value,
        noun: translate("pdf.noun.sesi"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "earned"),
    ),
};

// ── Pendapatan per Klien ────────────────────────────────────────────────────

type ClientRevenueRow = ClientRevenueAnalytics["table"][number];

// The Upgrade entry is unconditional because this report's Upgrade column is:
// without it, sorting by Upgrade would silently leave the PDF in server order
// under a caption naming a different column.
const CLIENT_REVENUE_META = (): SortMeta => ({
  bookings: { label: translate("analyticsPage.columns.bookings"), type: "number" },
  session_revenue: { label: translate("analyticsPage.columns.booking_revenue"), type: "number" },
  packages_bought: { label: translate("analyticsPage.columns.packages_bought"), type: "number" },
  memberships_bought: { label: translate("analyticsPage.columns.memberships_bought"), type: "number" },
  upgrades: { label: translate("analyticsPage.columns.upgrades"), type: "number" },
  purchase_revenue: { label: translate("analyticsPage.columns.sales"), type: "number" },
  __default: { label: translate("analyticsPage.sales.sort_default_client_value"), type: "number" },
});

export const clientRevenueSpec: DocSpec<ClientRevenueAnalytics, ClientRevenueRow> = {
  layout: "detail",
  limited: true,
  meta: CLIENT_REVENUE_META,
  table: (data) => data.table,
  columns: () =>
    columns<ClientRevenueRow>([
      { metric: "client", render: (r) => r.customer_name },
      { metric: "bookings", render: (r) => String(r.bookings) },
      { metric: "booking_revenue", render: (r) => formatCurrency(r.session_revenue) },
      { metric: "packages_bought", render: (r) => String(r.packages_bought) },
      { metric: "memberships_bought", render: (r) => String(r.memberships_bought) },
      // Always present, matching the screen. A dash means this client made
      // no upgrades in the period; any fee they did pay is inside Penjualan.
      { metric: "upgrades", render: (r) => formatUpgrades(r.upgrades) },
      { metric: "sales", render: (r) => formatCurrency(r.purchase_revenue) },
    ]),
  kpis: (data) => [
    {
      value: formatCurrency(data.total_session_revenue.value),
      label: translate("analyticsPage.sales.kpi_total_revenue"),
    },
    {
      value: formatCurrency(data.total_purchase_revenue.value),
      label: translate("analyticsPage.sales.kpi_total_sales"),
    },
  ],
  // The default order is a server composite; any explicit sort is "off it".
  defaultSort: { key: "__default", order: "descend" },
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.table_total,
        noun: translate("pdf.noun.klien"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "earned_cash"),
    ),
};

// ── Performa Klien ──────────────────────────────────────────────────────────

type ClientPerfRow = ClientAnalytics["table"][number];

const CLIENT_PERF_META = (): SortMeta => ({
  bookings: { label: translate("analyticsPage.columns.bookings"), type: "number" },
  no_shows: { label: translate("analyticsPage.columns.no_shows"), type: "number" },
  open_sessions: { label: translate("analyticsPage.columns.open_sessions"), type: "number" },
  private_sessions: { label: translate("analyticsPage.columns.private_sessions"), type: "number" },
});

export const clientPerformanceSpec: DocSpec<ClientAnalytics, ClientPerfRow> = {
  layout: "detail",
  limited: true,
  meta: CLIENT_PERF_META,
  table: (data) => data.table,
  columns: () =>
    columns<ClientPerfRow>([
      { metric: "client", render: (r) => r.customer_name },
      { metric: "bookings", render: (r) => String(r.bookings) },
      { metric: "no_shows", render: (r) => String(r.no_shows) },
      { metric: "open_sessions", render: (r) => String(r.open_sessions) },
      { metric: "private_sessions", render: (r) => String(r.private_sessions) },
      { metric: "last_session", render: (r) => formatDateOrDash(r.last_session) },
    ]),
  kpis: (data) => [
    { value: String(data.total_clients.value), label: translate("analyticsPage.client.kpi_total_clients") },
    { value: String(data.active_clients.value), label: translate("analyticsPage.client.kpi_active_clients") },
    { value: String(data.new_clients.value), label: translate("analyticsPage.client.kpi_new_clients") },
  ],
  defaultSort: { key: "bookings", order: "descend" },
  note: (data, shown, loaded, sortedOffDefault) =>
    truncationNote({
      shown,
      total: data.table_total,
      noun: translate("pdf.noun.klien"),
      loaded,
      sortedOffDefault,
    }),
};

// ── Pembelian Paket / Membership ────────────────────────────────────────────

type PurchasesRow = SalesPackagesAnalytics["table"][number];

// Every sortable screen column, or a sort on it would leave the PDF in
// server order under a caption naming it (see the note on CLIENT_REVENUE_META).
const PURCHASES_META = (): SortMeta => ({
  purchased_at: { label: translate("analyticsPage.columns.purchased_at"), type: "date" },
  price_before_tax: {
    label: translate("analyticsPage.columns.price_before_tax"),
    type: "number",
    // Mirrors the UI sorter: null only from an older server.
    value: (r: PurchasesRow) => r.price_before_tax ?? -1,
  },
  tax_amount: {
    label: translate("analyticsPage.columns.tax"),
    type: "number",
    value: (r: PurchasesRow) => r.tax_amount ?? 0,
  },
  price: { label: translate("analyticsPage.columns.total_paid"), type: "number" },
});

// The word the screen shows as a chip beside the name, in the same fixed
// English: a Rp 300.000 line against an Rp 800.000 package would otherwise
// read as a deep discount rather than the difference paid on an upgrade,
// and a voided sale would read as ordinary revenue. Voided wins when both
// apply: the new half of a CANCELLED upgrade is a voided sale that keeps its
// Upgrade label (the screen shows both chips; this cell holds the status).
// A void is otherwise refused on either half of an upgrade.
const purchaseStatusWord = (r: PurchasesRow) =>
  r.is_voided
    ? INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]
    : r.is_upgrade === true
      ? UPGRADE_LABEL
      : null;

// The product cell's second line: the status word, the payment method, or
// both joined with a middle dot ("Upgrade · Cash"); nothing when the sale
// has neither.
const purchaseSubLine = (r: PurchasesRow): string | null => {
  const parts = [purchaseStatusWord(r), r.payment_method_name ?? null].filter(
    (part): part is string => !!part,
  );
  return parts.length ? parts.join(" \u00B7 ") : null;
};

export const purchasesSpec = (
  type: "paket" | "membership",
): DocSpec<SalesPackagesAnalytics, PurchasesRow> => ({
  layout: "detail",
  limited: true,
  meta: PURCHASES_META,
  table: (data) => data.table,
  // The screen's ten columns do not fit one line each on A4 landscape (the
  // one-line figures alone leave about 70pt for the client and the package
  // together), so the PDF prints a designed two-line row: the client with
  // the transaction ID as its second line, the product with the status word
  // (the screen's chip) and the payment method as its second line, and the
  // date and the four money columns on one line each (no Kedaluwarsa, as on
  // the screen: a sales row lists the transaction, and the expiry lives in
  // the Kedaluwarsa reports and the Excel's fuller row). The same
  // information as the screen, every row the same height, about twelve rows
  // a page instead of seven. Diskon carries its source (the code, Loyalty or
  // Referral) under the amount, like the screen's two-line cell.
  columns: (data) =>
    columns<PurchasesRow>([
      {
        metric: "client",
        render: (r) => r.customer_name,
        sub: (r) => (r.transaction_number == null ? null : formatTransactionNumber("TRX", r.transaction_number)),
      },
      {
        metric: type === "membership" ? "membership" : "package",
        render: (r) => r.package_name,
        sub: purchaseSubLine,
      },
      { metric: "purchased_at", render: (r) => formatDateTime(r.purchased_at) },
      // Harga Jual less Diskon plus Pajak equals Total on every row (the
      // server's sale breakdown); a dash only from an older server.
      { metric: "price_before_tax", render: (r) => formatMoneyOrDash(r.price_before_tax) },
      {
        metric: "discount",
        // The minus tight against the amount, as the column has always
        // printed it.
        render: (r) =>
          discountSourceLabel(r) && r.discount_amount != null ? `−${formatCurrency(r.discount_amount)}` : "-",
        sub: (r) => (discountSourceLabel(r) && r.discount_amount != null ? discountSourceLabel(r) : null),
      },
      ...(data.tax?.any
        ? [{ metric: "tax" as const, render: (r: PurchasesRow) => (r.tax_amount ? formatCurrency(r.tax_amount) : "-") }]
        : []),
      { metric: "total_paid", render: (r) => formatCurrency(r.price) },
    ]),
  kpis: (data) => {
    return [
      { value: formatCurrency(data.total_revenue.value), label: translate("analyticsPage.sales.kpi_total_sales") },
      { value: String(data.packages_sold?.value ?? 0), label: translateWithVars("pdf.sold_kpi", { noun: productNounPlural(type) }) },
      // Only when the period contains one, matching the screen. Same i18n
      // key as the screen card; the `package` namespace is safe because the
      // count is never above zero for a membership.
      ...((data.upgrades?.value ?? 0) > 0
        ? [{ value: String(data.upgrades?.value ?? 0), label: translate("analyticsPage.package.kpi_upgrades") }]
        : []),
      // The screen card's own label ("Paket dengan Pendapatan Tertinggi").
      {
        value: data.best_seller.name ?? "-",
        label: translate(`analyticsPage.${type === "membership" ? "membership" : "package"}.kpi_purchases_best_seller`),
      },
    ];
  },
  defaultSort: { key: "purchased_at", order: "descend" },
  // total_rows, NOT packages_sold: the KPI excludes upgrades while the log
  // still lists them, so that would under-count the printed rows. The basis
  // line follows when a listed sale carried tax (plus the mixed-settings
  // sentence when the period spans more than one rule), measured by the
  // planner like the truncation note.
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.total_rows?.value ?? data.table.length,
        noun: translate("pdf.noun.pembelian"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "purchases"),
    ),
});

// ── Pemakaian Reward ────────────────────────────────────────────────────────

type RewardUsageRow = RewardUsageAnalytics["table"][number];

const REWARD_USAGE_META = (): SortMeta => ({
  used_at: { label: translate("analyticsPage.columns.date"), type: "date" },
  discount_amount: {
    label: translate("analyticsPage.columns.discount"),
    type: "number",
    // Mirrors the UI sorter: a credit has no discount value, so it sorts
    // below any real amount rather than tying with Rp 0.
    value: (r: RewardUsageRow) => r.discount_amount ?? -1,
  },
});

export const rewardUsageSpec: DocSpec<RewardUsageAnalytics, RewardUsageRow> = {
  layout: "detail",
  limited: true,
  meta: REWARD_USAGE_META,
  table: (data) => data.table,
  // The screen's seven columns in its order (Program and Status Booking
  // were sub lines when the page was portrait).
  columns: () =>
    columns<RewardUsageRow>([
      { metric: "date", render: (r) => formatDateTime(r.used_at) },
      { metric: "client", render: (r) => r.customer_name },
      { metric: "program", render: (r) => rewardProgramLabel(r.program) },
      { metric: "reward", render: (r) => rewardLabel(r) },
      { metric: "used_for", render: (r) => r.used_for },
      {
        metric: "discount",
        render: (r) => (r.discount_amount === null ? "-" : `−${formatCurrency(r.discount_amount)}`),
      },
      { metric: "booking_status", render: (r) => (r.status ? getBookingStatusLabel(r.status) : "-") },
    ]),
  kpis: (data) => [
    { value: String(data.credits_used.value), label: translate("analyticsPage.rewardUsage.kpi_credits_used") },
    { value: String(data.credits_expired.value), label: translate("analyticsPage.rewardUsage.kpi_credits_expired") },
    {
      value: formatCurrency(data.discount_value_used.value),
      label: translate("analyticsPage.rewardUsage.kpi_discount_given"),
    },
  ],
  defaultSort: { key: "used_at", order: "descend" },
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.table_total,
        noun: translate("pdf.noun.pemakaian"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "discounts"),
    ),
};

// ── Performa Kode Diskon ─────────────────────────────────────────────────────

type DiscountCodeRow = DiscountCodeAnalytics["table"][number];

const DISCOUNT_CODE_META = (): SortMeta => ({
  uses: { label: translate("analyticsPage.columns.uses"), type: "number" },
  clients: { label: translate("analyticsPage.columns.unique_clients"), type: "number" },
  new_clients: { label: translate("analyticsPage.columns.new_clients"), type: "number" },
  discount_given: { label: translate("analyticsPage.columns.total_discount"), type: "number" },
  revenue: { label: translate("analyticsPage.columns.sales"), type: "number" },
});

export const discountCodeSpec: DocSpec<DiscountCodeAnalytics, DiscountCodeRow> = {
  layout: "detail",
  limited: true,
  meta: DISCOUNT_CODE_META,
  table: (data) => data.table,
  // The screen's eight columns in its order (the rule and the status were
  // a sub line when the page was portrait).
  columns: (data) =>
    columns<DiscountCodeRow>([
      { metric: "code", render: (r) => r.code },
      { metric: "discount_rule", render: (r) => discountCodeRuleLabel(r) },
      { metric: "status", render: (r) => DISCOUNT_CODE_STATUS_LABELS[r.status] ?? r.status },
      { metric: "uses", render: (r) => String(r.uses) },
      { metric: "unique_clients", render: (r) => String(r.clients) },
      { metric: "new_clients", render: (r) => String(r.new_clients) },
      { metric: "total_discount", render: (r) => formatCurrency(r.discount_given) },
      // Pembatasan Admin: dropped for a financially restricted Admin, exactly
      // as the screen drops it, so the PDF prints what that admin can see. The
      // row picker plans pages from this same spec, so its count stays right
      // for the narrower table.
      ...(data.money_hidden
        ? []
        : [{ metric: "sales" as const, render: (r: DiscountCodeRow) => formatMoneyOrDash(r.revenue) }]),
    ]),
  kpis: (data) => [
    { value: formatCurrency(data.discount_given.value), label: translate("analyticsPage.discountCode.kpi_discount_given") },
    // Dropped with the column, never alone.
    ...(data.money_hidden
      ? []
      : [{
          value: formatCurrency(data.discounted_revenue?.value ?? 0),
          label: translate("analyticsPage.discountCode.kpi_revenue"),
        }]),
    { value: data.most_used?.code ?? "-", label: translate("analyticsPage.discountCode.kpi_most_used") },
  ],
  defaultSort: { key: "uses", order: "descend" },
  note: (data, shown, loaded, sortedOffDefault) =>
    joinNotes(
      truncationNote({
        shown,
        total: data.table_total,
        noun: translate("pdf.noun.kode"),
        loaded,
        sortedOffDefault,
      }),
      taxBasisNote(data, "discounts"),
    ),
};

